<template>
  <section class="admin-content">
    <Navbar />

    <main class="admin-main">
      <div class="bg-dark m-b-30">
        <div class="container">
          <div class="row p-b-60 p-t-60">
            <div class="col-md-8 text-center mx-auto text-white p-b-30">
              <h3 class="h4 text-white">
                <i class="fe fe-image mr-2"></i>
                Image Repair
              </h3>
              <p class="text-muted m-0">
                Scan the <code class="text-light">/images</code> folder for
                prefix-corrupted files and fix them one by one.
              </p>
            </div>
          </div>
        </div>
      </div>

      <section>
        <div class="container">

          <!-- Summary cards -->
          <div class="row m-b-20" v-if="candidates.length">
            <div class="col-md-4">
              <div class="card m-b-20 bg-warning">
                <div class="card-body text-dark">
                  <div class="pb-2">
                    <div class="avatar avatar-lg">
                      <div class="avatar-title bg-soft-warning rounded-circle">
                        <i class="fe fe-alert-triangle"></i>
                      </div>
                    </div>
                  </div>
                  <p class="h5 m-0">Corrupted Files</p>
                  <h1 class="fw-400">{{ candidates.length }}</h1>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="card m-b-20 bg-success">
                <div class="card-body text-white">
                  <div class="pb-2">
                    <div class="avatar avatar-lg">
                      <div class="avatar-title bg-soft-success rounded-circle">
                        <i class="fe fe-check-circle"></i>
                      </div>
                    </div>
                  </div>
                  <p class="h5 m-0">Repaired</p>
                  <h1 class="fw-400">{{ repairedCount }}</h1>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="card m-b-20 bg-info">
                <div class="card-body text-dark">
                  <div class="pb-2">
                    <div class="avatar avatar-lg">
                      <div class="avatar-title bg-soft-info rounded-circle">
                        <i class="fe fe-clock"></i>
                      </div>
                    </div>
                  </div>
                  <p class="h5 m-0">Remaining</p>
                  <h1 class="fw-400">{{ pendingCount }}</h1>
                </div>
              </div>
            </div>
          </div>

          <!-- Action bar -->
          <div class="row m-b-20">
            <div class="col-md-12 d-flex align-items-center justify-content-between">
              <div>
                <button
                  class="btn btn-dark mr-2"
                  @click="loadCandidates"
                  :disabled="loading || isBulkFixing"
                >
                  <i class="fe fe-refresh-cw mr-1"></i>
                  {{ loading ? 'Scanning...' : 'Scan for Corrupted Files' }}
                </button>
                <button
                  class="btn btn-outline-dark mr-2"
                  @click="toggleSelectAll"
                  :disabled="!selectableCount || isBulkFixing"
                >
                  <i class="fe fe-check-square mr-1"></i>
                  {{ allSelectableSelected ? 'Unmark All' : 'Mark All' }}
                </button>
                <button
                  class="btn btn-primary mr-2"
                  @click="fixSelectedFiles"
                  :disabled="!selectedCount || isBulkFixing"
                >
                  <i class="fe fe-tool mr-1"></i>
                  {{ isBulkFixing ? 'Fixing Selected...' : `Fix Selected (${selectedCount})` }}
                </button>
                <button
                  class="btn btn-outline-primary mr-2"
                  @click="downloadQueryCsv"
                  :disabled="!candidates.length"
                >
                  <i class="fe fe-download mr-1"></i>
                  Download Query CSV
                </button>
                <button
                  class="btn btn-outline-success"
                  @click="downloadFixedCsv"
                  :disabled="!repairLog.length"
                >
                  <i class="fe fe-download mr-1"></i>
                  Download Fixed CSV
                </button>
              </div>

              <span v-if="directory" class="text-muted small">
                <i class="fe fe-folder mr-1"></i>
                Scanning: <code>{{ directory }}</code>
              </span>
            </div>
            <div class="col-md-12 m-t-10" v-if="candidates.length">
              <span class="text-muted small">
                Bulk fix runs one file every {{ fixIntervalMs / 1000 }} seconds to reduce too-many-attempts errors.
              </span>
            </div>
          </div>

          <!-- Empty state -->
          <div class="row" v-if="!loading && scanned && candidates.length === 0">
            <div class="col-12">
              <div class="card">
                <div class="card-body text-center p-t-40 p-b-40">
                  <i class="fe fe-check-circle text-success" style="font-size:3rem;"></i>
                  <p class="h5 m-t-20">No corrupted files found.</p>
                  <p class="text-muted">All images in the folder appear to be healthy.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- File table -->
          <div class="row" v-if="candidates.length">
            <div class="col-12">
              <div class="card m-b-30">
                <div class="card-header">
                  <h5 class="card-title m-0">
                    Candidate Files
                    <span class="badge badge-warning ml-2">{{ candidates.length }}</span>
                  </h5>
                </div>
                <div class="table-responsive">
                  <table class="table table-striped table-hover m-0">
                    <thead class="thead-dark">
                      <tr>
                        <th class="text-center" style="width: 50px;">
                          <input
                            type="checkbox"
                            :checked="allSelectableSelected"
                            :disabled="!selectableCount || isBulkFixing"
                            @change="toggleSelectAll"
                          >
                        </th>
                        <th>#</th>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th class="text-center">Corrupt Prefix Bytes</th>
                        <th class="text-center">Status</th>
                        <th class="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(file, index) in candidates"
                        :key="file.file_name"
                        :class="rowClass(file)"
                      >
                        <td class="text-center">
                          <input
                            type="checkbox"
                            :disabled="!isFileSelectable(file) || isBulkFixing"
                            :checked="isSelected(file.file_name)"
                            @change="toggleFileSelection(file.file_name)"
                          >
                        </td>
                        <td>{{ index + 1 }}</td>
                        <td>
                          <span class="text-monospace small">{{ file.file_name }}</span>
                        </td>
                        <td>
                          <span class="badge badge-secondary">{{ file.detected_type }}</span>
                        </td>
                        <td>{{ formatBytes(file.size) }}</td>
                        <td class="text-center">
                          <span class="badge badge-warning">{{ file.prefix_bytes }}</span>
                        </td>
                        <td class="text-center">
                          <span
                            v-if="file._status === 'repaired'"
                            class="badge badge-success"
                          ><i class="fe fe-check mr-1"></i>Repaired</span>
                          <span
                            v-else-if="file._status === 'error'"
                            class="badge badge-danger"
                            :title="file._error"
                          ><i class="fe fe-x mr-1"></i>Failed</span>
                          <span
                            v-else-if="file._status === 'fixing'"
                            class="badge badge-info"
                          ><i class="fe fe-loader mr-1"></i>Fixing...</span>
                          <span
                            v-else
                            class="badge badge-warning"
                          ><i class="fe fe-alert-circle mr-1"></i>Pending</span>
                        </td>
                        <td class="text-center">
                          <button
                            v-if="file._status !== 'repaired'"
                            class="btn btn-sm btn-primary"
                            :disabled="file._status === 'fixing' || isBulkFixing"
                            @click="fixFile(file)"
                          >
                            <i class="fe fe-tool mr-1"></i>
                            Fix
                          </button>
                          <span v-else class="text-muted small">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Repair log -->
          <div class="row" v-if="repairLog.length">
            <div class="col-12">
              <div class="card m-b-30">
                <div class="card-header">
                  <h5 class="card-title m-0">
                    Repair Log
                    <span class="badge badge-success ml-2">{{ repairLog.length }}</span>
                  </h5>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm m-0">
                    <thead class="thead-light">
                      <tr>
                        <th>File</th>
                        <th>Type</th>
                        <th class="text-center">Prefix Removed (bytes)</th>
                        <th>Backup</th>
                        <th>Fixed At</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="entry in repairLog" :key="entry.file_name + entry.fixed_at">
                        <td class="text-monospace small">{{ entry.file_name }}</td>
                        <td><span class="badge badge-secondary">{{ entry.detected_type }}</span></td>
                        <td class="text-center">
                          <span class="badge badge-info">{{ entry.removed_prefix_bytes }}</span>
                        </td>
                        <td class="text-monospace small text-muted">{{ entry.backup_path }}</td>
                        <td class="small text-muted">{{ formatDate(entry.fixed_at) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

export default {
  name: "ImageRepairView",
  components: { Navbar },
  created() {
    this.loadRepairLog();
  },
  data() {
    return {
      loading: false,
      scanned: false,
      directory: "",
      candidates: [],
      repairLog: [],
      queryTimestamp: null,
      repairLogStorageKey: "image-repair-fixed-log-v1",
      selectedFiles: {},
      isBulkFixing: false,
      fixIntervalMs: 2000,
    };
  },
  computed: {
    repairedCount() {
      return this.candidates.filter((f) => f._status === "repaired").length;
    },
    pendingCount() {
      return this.candidates.filter(
        (f) => f._status !== "repaired"
      ).length;
    },
    selectableCount() {
      return this.candidates.filter((f) => this.isFileSelectable(f)).length;
    },
    selectedCount() {
      return this.candidates.filter(
        (f) => this.isFileSelectable(f) && this.selectedFiles[f.file_name]
      ).length;
    },
    allSelectableSelected() {
      if (!this.selectableCount) return false;
      return this.candidates
        .filter((f) => this.isFileSelectable(f))
        .every((f) => this.selectedFiles[f.file_name]);
    },
  },
  methods: {
    async loadCandidates() {
      this.loading = true;
      this.scanned = false;
      this.candidates = [];
      this.selectedFiles = {};
      try {
        const res = await this.axios.get("/api/v1/image-repair/candidates");
        this.queryTimestamp = new Date().toISOString();
        this.directory = res.data.directory || "";
        this.candidates = (res.data.files || []).map((f) => ({
          ...f,
          _status: "pending",
          _error: null,
        }));
        this.scanned = true;

        // Export the scan result immediately after a successful query.
        this.downloadQueryCsv();

        if (this.candidates.length === 0) {
          this.$toasted.success("No corrupted files found.", {
            duration: 4000,
            position: "top-right",
          });
        } else {
          this.$toasted.info(
            `Found ${this.candidates.length} file(s) with prefix corruption.`,
            { duration: 4000, position: "top-right" }
          );
        }
      } catch (err) {
        const msg =
          err.response?.data?.message || "Failed to scan directory.";
        this.$toasted.error(msg, { duration: 5000, position: "top-right" });
      } finally {
        this.loading = false;
      }
    },

    async fixFile(file, options = { notify: true }) {
      this.$set(file, "_status", "fixing");
      this.$set(file, "_error", null);
      try {
        const res = await this.axios.post(
          `/api/v1/image-repair/fix/${encodeURIComponent(file.file_name)}`
        );
        this.$set(file, "_status", "repaired");
        const result = res.data.file || {};
        this.repairLog.unshift({
          file_name: result.file_name || file.file_name,
          detected_type: result.detected_type || file.detected_type,
          removed_prefix_bytes: result.removed_prefix_bytes ?? file.prefix_bytes,
          backup_path: result.backup_path || "—",
          fixed_at: new Date().toISOString(),
        });
        this.persistRepairLog();
        this.$delete(this.selectedFiles, file.file_name);
        if (options.notify) {
          this.$toasted.success(
            `Repaired: ${file.file_name}`,
            { duration: 4000, position: "top-right" }
          );
        }
        return true;
      } catch (err) {
        const msg =
          err.response?.data?.message || "Failed to repair file.";
        this.$set(file, "_status", "error");
        this.$set(file, "_error", msg);
        if (options.notify) {
          this.$toasted.error(`${file.file_name}: ${msg}`, {
            duration: 6000,
            position: "top-right",
          });
        }
        return false;
      }
    },

    isFileSelectable(file) {
      return file._status !== "repaired" && file._status !== "fixing";
    },

    isSelected(fileName) {
      return Boolean(this.selectedFiles[fileName]);
    },

    toggleFileSelection(fileName) {
      if (this.selectedFiles[fileName]) {
        this.$delete(this.selectedFiles, fileName);
        return;
      }
      this.$set(this.selectedFiles, fileName, true);
    },

    toggleSelectAll() {
      if (this.allSelectableSelected) {
        this.candidates.forEach((file) => {
          if (this.isFileSelectable(file)) {
            this.$delete(this.selectedFiles, file.file_name);
          }
        });
        return;
      }

      this.candidates.forEach((file) => {
        if (this.isFileSelectable(file)) {
          this.$set(this.selectedFiles, file.file_name, true);
        }
      });
    },

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async fixSelectedFiles() {
      const filesToFix = this.candidates.filter(
        (file) => this.isFileSelectable(file) && this.selectedFiles[file.file_name]
      );

      if (!filesToFix.length) {
        this.$toasted.info("Select at least one file to bulk-fix.", {
          duration: 4000,
          position: "top-right",
        });
        return;
      }

      this.isBulkFixing = true;
      let successCount = 0;
      let failedCount = 0;

      try {
        for (let index = 0; index < filesToFix.length; index += 1) {
          const isSuccess = await this.fixFile(filesToFix[index], { notify: false });
          if (isSuccess) {
            successCount += 1;
          } else {
            failedCount += 1;
          }

          if (index < filesToFix.length - 1) {
            await this.wait(this.fixIntervalMs);
          }
        }

        this.$toasted.info(
          `Bulk fix completed. Success: ${successCount}, Failed: ${failedCount}.`,
          { duration: 6000, position: "top-right" }
        );
      } finally {
        this.isBulkFixing = false;
      }
    },

    rowClass(file) {
      if (file._status === "repaired") return "table-success";
      if (file._status === "error") return "table-danger";
      if (file._status === "fixing") return "table-info";
      return "";
    },

    formatBytes(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },

    loadRepairLog() {
      try {
        const raw = localStorage.getItem(this.repairLogStorageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        this.repairLog = parsed;
      } catch (err) {
        this.repairLog = [];
      }
    },

    persistRepairLog() {
      localStorage.setItem(
        this.repairLogStorageKey,
        JSON.stringify(this.repairLog)
      );
    },

    escapeCsv(value) {
      const cell = value === null || value === undefined ? "" : String(value);
      return `"${cell.replace(/"/g, '""')}"`;
    },

    buildCsv(headers, rows) {
      const headerLine = headers.map((header) => this.escapeCsv(header)).join(",");
      const rowLines = rows.map((row) =>
        row.map((value) => this.escapeCsv(value)).join(",")
      );
      return [headerLine, ...rowLines].join("\n");
    },

    triggerCsvDownload(fileName, csv) {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    },

    downloadFixedCsv() {
      const headers = [
        "file_name",
        "detected_type",
        "removed_prefix_bytes",
        "backup_path",
        "fixed_at",
      ];
      const rows = this.repairLog.map((entry) => [
        entry.file_name,
        entry.detected_type,
        entry.removed_prefix_bytes,
        entry.backup_path,
        entry.fixed_at,
      ]);
      const csv = this.buildCsv(headers, rows);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      this.triggerCsvDownload(`fixed-images-${stamp}.csv`, csv);
    },

    downloadQueryCsv() {
      const headers = [
        "file_name",
        "detected_type",
        "mime_type",
        "size_bytes",
        "prefix_bytes",
        "status",
        "queried_at",
      ];
      const queriedAt = this.queryTimestamp || new Date().toISOString();
      const rows = this.candidates.map((entry) => [
        entry.file_name,
        entry.detected_type,
        entry.mime_type,
        entry.size,
        entry.prefix_bytes,
        entry._status,
        queriedAt,
      ]);
      const csv = this.buildCsv(headers, rows);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      this.triggerCsvDownload(`queried-corrupt-images-${stamp}.csv`, csv);
    },
  },
};
</script>
